// E-post-settings – taskJobs/projects-style redesign
import { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useNavigate } from "react-router-dom";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import {
  customerEmails,
  getEmailSettings,
  getEmailTemplate,
  deleteEmailTemplate,
} from "api/apiService";
import { notifyCenter } from "utils/toast";
import ConfirmDeleteModal from "./ConfirmDeleteModal";

// email_type → (Norwegian label, chip color)
const EMAIL_TYPE_META = {
  order_creation:        { label: "Ordreoppretting",  color: "success" },
  status_change:         { label: "Statusendring",    color: "info" },
  price_change_by_admin: { label: "Prisendring",      color: "warning" },
  delivery_date_changed: { label: "Leveransedato",    color: "warning" },
  payment_received:      { label: "Betaling mottatt", color: "success" },
  order_refunded:        { label: "Refundert",        color: "error" },
  order_note_added:      { label: "Ordrenotat",       color: "info" },
  custom:                { label: "Tilpasset",        color: "default" },
};

function emailTypeMeta(value) {
  return EMAIL_TYPE_META[value] || { label: value || "—", color: "default" };
}

export default function EmailTemplate() {
  const navigate = useNavigate();

  const [enabled, setEnabled] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function fetchTemplates(p = page) {
    setLoading(true);
    setError(null);
    try {
      const res = await getEmailTemplate({ limit: pageSize, page_number: p });
      if (res?.status) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setTotalRecords(res?.data_info?.total_records ?? 0);
        setTotalPages(res?.data_info?.total_pages ?? 1);
      } else {
        setError(res?.message || "Kunne ikke hente e-postmaler");
        setRows([]);
      }
    } catch (e) {
      setError(e?.message || "Nettverksfeil");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTemplates(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Backend returnerer customer_emails som string "1"|"0" (for WP-paritet),
  // ikke boolean. `!!"0"` er true → derfor stod toggle alltid på "PÅ" etter
  // reload uansett lagret verdi. Aksepter alle tre formater defensivt.
  function parseEmailEnabled(v) {
    if (v === true || v === 1 || v === "1") return true;
    if (v === false || v === 0 || v === "0") return false;
    return false; // ukjent verdi → trygt default = av
  }

  useEffect(() => {
    (async () => {
      try {
        const result = await getEmailSettings();
        setEnabled(parseEmailEnabled(result?.customer_emails));
      } catch (err) {
        notifyCenter("error", "Kunne ikke hente e-postinnstillinger", { duration: 3000 });
      }
    })();
  }, []);

  async function handleToggle() {
    const newValue = !enabled;
    setEnabled(newValue);
    try {
      const res = await customerEmails(newValue);
      // Reflect backend's authoritative value back into UI in case server
      // rounded the value differently (e.g. coerced to "1"/"0").
      if (res && "customer_emails" in res) {
        setEnabled(parseEmailEnabled(res.customer_emails));
      }
      notifyCenter("success", `E-post er nå ${newValue ? "PÅ" : "AV"}`, { duration: 2000 });
    } catch (err) {
      notifyCenter("error", "Noe gikk galt under lagringen", { duration: 3000 });
      // revert
      setEnabled(!newValue);
    }
  }


  function openEdit(id) {
    navigate(`/email-settings/edit-template/${id}`);
  }

  function handleAskDelete(id) {
    setDeleteId(id);
    setConfirmOpen(true);
  }

  async function handleConfirmDelete() {
    setConfirmOpen(false);
    if (!deleteId) return;
    setLoading(true);
    try {
      const res = await deleteEmailTemplate(deleteId);
      if (res?.status) {
        notifyCenter("success", "Malen ble slettet", { duration: 2000 });
        setDeleteId(null);
        fetchTemplates(page);
      } else {
        notifyCenter("error", "Kunne ikke slette malen", { duration: 3000 });
      }
    } catch (err) {
      notifyCenter("error", "En feil oppstod ved sletting av malen", { duration: 3000 });
    } finally {
      setLoading(false);
    }
  }

  const offset = useMemo(() => (page - 1) * pageSize, [page]);

  return (
    <DashboardLayout>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      <SoftBox py={3} px={{ xs: 1, md: 2 }}>
        {/* Kundemeldinger-toggle */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={(t) => ({
                  background: `linear-gradient(195deg, ${t.palette.info.main}, ${t.palette.info.dark || t.palette.info.main})`,
                  color: "#fff",
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  display: "grid",
                  placeItems: "center",
                })}
              >
                <MailOutlineIcon fontSize="small" />
              </Box>
              <Box>
                <SoftTypography variant="button" fontWeight="bold" sx={{ textTransform: "none", fontSize: 14 }}>
                  Kundemeldinger
                </SoftTypography>
                <SoftTypography variant="caption" color="text" display="block">
                  Aktiverer/deaktiverer alle automatiske e-poster til kunder. E-poster du sender selv, går alltid ut.
                </SoftTypography>
              </Box>
            </Grid>
            <Grid item display="flex" alignItems="center" gap={1.5}>
              {enabled !== null && (
                <>
                  {/* AV-label til venstre, alltid synlig — fades ned naar paa */}
                  <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      color: !enabled ? "error.main" : "grey.400",
                      transition: "color 150ms",
                      letterSpacing: 0.5,
                    }}
                  >
                    AV
                  </SoftTypography>
                  <Switch
                    checked={enabled}
                    onChange={handleToggle}
                    color={enabled ? "success" : "default"}
                  />
                  {/* PA-label til hoyre, alltid synlig — fades ned naar av */}
                  <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      color: enabled ? "success.main" : "grey.400",
                      transition: "color 150ms",
                      letterSpacing: 0.5,
                    }}
                  >
                    PÅ
                  </SoftTypography>
                </>
              )}
            </Grid>
          </Grid>
        </Card>

        {/* E-postmaler */}
        <Card sx={{ position: "relative" }}>
          {/* Header */}
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            p={3}
          >
            <SoftBox>
              <SoftTypography variant="h5" fontWeight="bold">
                E-postmaler
              </SoftTypography>
              <SoftTypography variant="button" color="text">
                Maler som sendes ved ordre-hendelser (statusendring, betaling, refusjon m.m.)
              </SoftTypography>
            </SoftBox>
            <SoftButton
              variant="gradient"
              color="info"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => navigate("/email-settings/create-template")}
            >
              Ny mal
            </SoftButton>
          </SoftBox>
          <Divider sx={{ my: 0 }} />

          {error && (
            <SoftBox px={3} py={2}>
              <SoftTypography variant="button" color="error">
                {error}
              </SoftTypography>
            </SoftBox>
          )}

          {/* Table */}
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                width: "100%",
                minWidth: 1000,
                tableLayout: "fixed",
                "& thead": {
                  display: "table-header-group",
                  padding: 0,
                  borderRadius: 0,
                },
                "& tbody": { display: "table-row-group" },
                "& tr": { display: "table-row" },
                "& td, & th": {
                  display: "table-cell",
                  padding: "12px 14px",
                  borderBottom: (t) => `1px solid ${t.palette.grey[200]}`,
                  fontSize: 13,
                  verticalAlign: "middle",
                },
                "& thead th": {
                  backgroundColor: (t) => t.palette.grey[100],
                  color: (t) => t.palette.text.secondary,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  fontSize: 11,
                  letterSpacing: 0.4,
                  whiteSpace: "nowrap",
                },
                "& tbody tr": { cursor: "pointer" },
                "& tbody tr:hover": {
                  backgroundColor: (t) => t.palette.grey[50],
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "6%" }}>ID</TableCell>
                  <TableCell sx={{ width: "26%" }}>Mal</TableCell>
                  <TableCell sx={{ width: "18%" }}>Type</TableCell>
                  <TableCell sx={{ width: "auto" }}>Emne</TableCell>
                  <TableCell sx={{ width: "16%" }}>Sendes til</TableCell>
                  <TableCell sx={{ width: "10%" }} align="right">Handlinger</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow sx={{ cursor: "default !important" }}>
                    <TableCell colSpan={6} sx={{ py: 6 }}>
                      <SoftBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <InboxOutlinedIcon sx={(t) => ({ fontSize: 48, color: t.palette.grey[400] })} />
                        <SoftTypography variant="body2" color="secondary">
                          Ingen e-postmaler enda – opprett den første
                        </SoftTypography>
                        <SoftButton
                          variant="gradient"
                          color="info"
                          size="small"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={() => navigate("/email-settings/create-template")}
                          sx={{ mt: 1 }}
                        >
                          Ny mal
                        </SoftButton>
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((t) => {
                  const typeMeta = emailTypeMeta(t.email_type);
                  return (
                    <TableRow key={t.id} hover onClick={() => openEdit(t.id)}>
                      <TableCell sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                        #{t.id}
                      </TableCell>
                      <TableCell>
                        <SoftBox display="flex" alignItems="center" gap={0.75}>
                          <SoftTypography
                            variant="button"
                            fontWeight="medium"
                            sx={{ textTransform: "none", fontSize: 13 }}
                          >
                            {t.template_name || "—"}
                          </SoftTypography>
                          {/* Liten chip som viser om duplikater hindres for denne malen.
                              Default er PA — viser kun "Sendes flere ganger" naar admin
                              eksplisitt har skrudd den AV (mer informativt). */}
                          {t.prevent_duplicates === false && (
                            <Tooltip title="Sendes hver gang triggeren oppstår — ingen duplikat-skipping">
                              <Chip
                                label="Sendes flere ganger"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                              />
                            </Tooltip>
                          )}
                        </SoftBox>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={typeMeta.label}
                          color={typeMeta.color}
                          size="small"
                          sx={{ fontWeight: 600, height: 22 }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: (theme) => theme.palette.text.secondary,
                          fontSize: 12,
                        }}
                      >
                        {t.subject || "—"}
                      </TableCell>
                      <TableCell>
                        <SoftBox display="flex" gap={0.5} flexWrap="wrap">
                          {(t.send_to || "")
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean)
                            .map((role) => (
                              <Chip
                                key={role}
                                label={role}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                              />
                            ))}
                        </SoftBox>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="Rediger">
                          <IconButton size="small" onClick={() => openEdit(t.id)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Slett">
                          <IconButton
                            size="small"
                            onClick={() => handleAskDelete(t.id)}
                            sx={{ color: "error.main" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer pagination */}
          {rows.length > 0 && (
            <>
              <Divider sx={{ my: 0 }} />
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                p={3}
              >
                <SoftTypography variant="caption" color="secondary">
                  Viser {rows.length ? offset + 1 : 0}
                  {"–"}
                  {offset + rows.length} av {totalRecords}
                </SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || loading}
                  >
                    Forrige
                  </SoftButton>
                  <SoftTypography variant="caption" color="text" px={1}>
                    Side {page} / {totalPages}
                  </SoftTypography>
                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages || loading}
                  >
                    Neste
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </>
          )}

          {loading && (
            <SoftBox
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
                borderRadius: "inherit",
              }}
            >
              <CircularProgress color="info" size={36} />
            </SoftBox>
          )}
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}
